import React from "react";
import styled from "styled-components";
import { Row, Col } from "react-bootstrap";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import ReactMarkdown from "react-markdown";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";

/**
 * App download section with phone mockup on the right.
 * Used for WSOP+ app promotion section.
 */
const TournamentAppSection = ({ appSection }) => {
  if (!appSection) return null;
  const rawBgImage = useResponsiveImageUrl(appSection.bgImage || "");
  const bgImage = appSection.bgImage ? rawBgImage : null;

  return (
    <AppSectionContainer
      bgColor={appSection.bgColor}
      bgImage={bgImage}
      overlayColor={appSection.overlayColor}
    >
      <Row className="align-items-center h-100">
        <Col lg={7} md={12} className="h-100">
          <AppContent>
            {appSection.title && (
              <AppTitle titleColor={appSection.titleColor}>
                <ReactMarkdown>{appSection.title}</ReactMarkdown>
              </AppTitle>
            )}
            {appSection.content && (
              <AppText
                textColor={appSection.textColor}
                linkColor={appSection.linkColor}
              >
                <ReactMarkdown>{appSection.content}</ReactMarkdown>
              </AppText>
            )}
          </AppContent>
        </Col>
        {appSection.image && (
          <Col lg={5} md={12} className="h-100" style={{ maxHeight: "400px" }}>
            <AppImageContainer>
              <ResponsiveImage
                src={appSection.image}
                alt={appSection.imageAlt || "App Mockup"}
              />
            </AppImageContainer>
          </Col>
        )}
      </Row>
    </AppSectionContainer>
  );
};

const AppSectionContainer = styled.div`
  background: ${(props) => props.bgColor || "#F5F5F5"};
  ${(props) =>
    props.bgImage &&
    `
    background-image: url(${props.bgImage});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  `}
  border-radius: 20px;
  padding: 0 2rem;
  margin: 2rem 0;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  height: 100%;
  max-height: 400px;

  &:before {
    content: "";
    position: absolute;
    inset: 0;
    background: ${(props) => props.overlayColor || "transparent"};
    border-radius: 20px;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 992px) {
    height: auto;
    padding: 1.5rem;
    max-height: fit-content;
  }
`;

const AppContent = styled.div`
  padding-right: 2.25rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-height: 400px;

  @media (max-width: 992px) {
    padding-right: 0;
    margin-bottom: 1.5rem;
    height: auto;
  }
`;

const AppTitle = styled.div`
  color: ${(props) => props.titleColor || "#000000"};
  font-weight: 700;
  font-size: 1.5rem;

  h3,
  h4 {
    color: ${(props) => props.titleColor || "#000000"};
    margin: 0;
    font-size: 1.5rem;
  }

  @media (max-width: 768px) {
    font-size: 1.35rem;

    h3,
    h4 {
      font-size: 1.35rem;
    }
  }
`;

const AppText = styled.div`
  color: ${(props) => props.textColor || "#333333"};
  line-height: 1.6;
  font-size: 0.95rem;
  height: 100%;

  p {
    margin-bottom: 0.75rem;
  }

  strong {
    font-weight: 700;
    color: ${(props) => props.textColor || "#000000"};
  }

  a {
    color: ${(props) => props.linkColor || "#cc0000"};
    font-weight: 700;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const AppImageContainer = styled.div`
  text-align: right;
  position: relative;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  height: 100%;

  img {
    height: auto;
    max-height: 740px;
    object-fit: contain;
    margin-right: 2rem;
    transform: translate(2rem, -1rem);
  }

  @media (max-width: 992px) {
    text-align: center;
    justify-content: center;
    align-items: center;

    img {
      max-width: 115%;
      max-height: 380px;
      margin-right: 0;
      transform: none;
    }
  }
`;

export default TournamentAppSection;
